import { lazy, Suspense } from 'react'
import { createBrowserRouter } from 'react-router'
import { AppShell } from './AppShell'
import { DashboardPage } from '../pages/DashboardPage'
import { AccountPage } from '../pages/AccountPage'
import { AnnouncementsPage } from '../pages/AnnouncementsPage'
import { CalendarPage } from '../pages/CalendarPage'
import { MorePage, NotFoundPage } from '../pages/PlaceholderPages'
import { PortfolioPage } from '../pages/PortfolioPage'
import { WatchlistPage } from '../pages/WatchlistPage'
import { AlertsPage } from '../pages/AlertsPage'
import { AdminPage } from '../pages/AdminPage'
import { PricingPage } from '../pages/PricingPage'
import { BillingPage } from '../pages/BillingPage'
import { SearchPage } from '../pages/SearchPage'
import { LoginPage } from '../pages/LoginPage'
import { RankedPage } from '../pages/RankedPage'
import { EmptyState, Skeleton } from '../ui'
import { useSession } from '../lib/session'
import type { ReactNode } from 'react'

/** Internal pages (component gallery) are for admins only. */
function AdminOnly({ children }: { children: ReactNode }) {
  const { me } = useSession()
  if (me === undefined) return <div style={{ padding: 24 }}><Skeleton lines={2} label="Loading" /></div>
  if (me?.user.role !== 'admin') return <div style={{ padding: 24 }}><EmptyState kind="error" title="Page not found" /></div>
  return <>{children}</>
}

// The gallery is a separate chunk so it never weighs on real pages.
const StockPage = lazy(() => import('../pages/StockPage').then(m => ({ default: m.StockPage })))
const UiGallery = lazy(() => import('../pages/UiGallery').then(m => ({ default: m.UiGallery })))

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    path: '/',
    element: <AppShell />,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'announcements', element: <AnnouncementsPage /> },
      { path: 'ranked', element: <RankedPage /> },
      { path: 'stock/:symbol', element: <Suspense fallback={<div style={{ padding: 24 }}><Skeleton lines={4} label="Loading stock" /></div>}><StockPage /></Suspense> },
      { path: 'calendar', element: <CalendarPage /> },
      { path: 'portfolio', element: <PortfolioPage /> },
      { path: 'alerts', element: <AlertsPage /> },
      { path: 'account', element: <AccountPage /> },
      { path: 'watchlist', element: <WatchlistPage /> },
      { path: 'more', element: <MorePage /> },
      { path: 'admin', element: <AdminPage /> },
      { path: 'admin/:tab', element: <AdminPage /> },
      { path: 'pricing', element: <PricingPage /> },
      { path: 'billing', element: <BillingPage /> },
      { path: 'search', element: <SearchPage /> },
      { path: 'ui', element: <AdminOnly><Suspense fallback={<div style={{ padding: 24 }}><Skeleton lines={4} label="Loading gallery" /></div>}><UiGallery /></Suspense></AdminOnly> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
])
